import { z } from 'zod';
import { ReactNode } from 'react';
import { ProviderConfig } from './configs';

// Complete field definition with form, validation, and display
export interface FieldDefinition {
    id: string;
    // Component factory function. The returned component should accept a `description` prop
    // which will be injected via React.cloneElement when getDescription is present
    component: (prefix: string, config?: ProviderConfig) => JSX.Element;
    // Optional key to use for the schema
    schemaKey?: string;
    schema: z.ZodTypeAny | ((config?: ProviderConfig) => z.ZodTypeAny);
    reviewLabel: string | ((config?: ProviderConfig) => string);
    reviewValue: (data: any) => ReactNode;
    // Optional function to generate description text for the field (e.g., legal disclosures)
    // When present, the description will be injected into the component via cloneElement
    getDescription?: (
        config: ProviderConfig,
        disclosures: { phone?: string; email?: string },
    ) => string | undefined;
}

// Provider field override structure
export interface ProviderFieldOverride {
    insertions?: {
        after: string;
        fields: string[];
    }[];
    removals?: string[];
    additions?: string[];
}

// Base field keys for each form section
export type PersonalInfoFieldKey =
    | 'firstName'
    | 'lastName'
    | 'email'
    | 'primaryPhone'
    | 'dateOfBirth'
    | 'ssn'
    | 'grossMonthlyIncome'
    | 'annualIncome';

export type JobDetailsFieldKey = 'address' | 'residenceType' | 'industryType';

/**
 * Returns base fields that appear for ALL providers (in default order).
 * When useAnnualIncome is true, returns 'annualIncome' instead of 'grossMonthlyIncome'.
 */
export function getPersonalInfoBaseFields(
    useAnnualIncome?: boolean,
): readonly PersonalInfoFieldKey[] {
    return [
        'firstName',
        'lastName',
        'email',
        'primaryPhone',
        'dateOfBirth',
        'ssn',
        useAnnualIncome ? 'annualIncome' : 'grossMonthlyIncome',
    ] as const;
}

// Note: The actual field registry with React components is defined in application-shared
// to avoid circular dependencies. This file exports only types and utilities.

/**
 * Resolves the field order by applying provider overrides and optional Turns overrides
 * @param baseOrder - The base field order array
 * @param overrides - Provider-specific field overrides
 * @param turnsOverrides - Optional Turns co-application field overrides (applied after provider overrides)
 * @returns Resolved field order array
 */
export function resolveFieldOrder(
    baseOrder: readonly string[],
    overrides?: ProviderFieldOverride,
    turnsOverrides?: ProviderFieldOverride,
): string[] {
    let order = [...baseOrder];

    // Apply provider overrides
    if (overrides) {
        // Apply removals first
        if (overrides.removals) {
            order = order.filter(field => !overrides.removals!.includes(field));
        }

        // Apply insertions
        if (overrides.insertions) {
            for (const insertion of overrides.insertions) {
                const index = order.indexOf(insertion.after);
                if (index !== -1) {
                    order.splice(index + 1, 0, ...insertion.fields);
                }
            }
        }

        // Apply additions (append to end)
        if (overrides.additions) {
            order.push(...overrides.additions);
        }
    }

    // Apply Turns overrides if provided (when Turns co-application is enabled)
    if (turnsOverrides) {
        // Apply removals
        if (turnsOverrides.removals) {
            order = order.filter(field => !turnsOverrides.removals!.includes(field));
        }

        // Apply insertions
        if (turnsOverrides.insertions) {
            for (const insertion of turnsOverrides.insertions) {
                const index = order.indexOf(insertion.after);
                if (index !== -1) {
                    order.splice(index + 1, 0, ...insertion.fields);
                }
            }
        }

        // Apply additions
        if (turnsOverrides.additions) {
            order.push(...turnsOverrides.additions);
        }
    }

    return order;
}
